import { Helmet } from 'react-helmet-async';
import { previsaoTempoWidget } from '@/lib/widgets';
import { useTranslation } from '@/hooks/useTranslation';
import { Sun, Cloud, CloudRain, Thermometer, Droplets, Waves, ArrowUpRight } from 'lucide-react';

/* ═══════════════════════════════════════════
   PREVISÃO DO TEMPO — Widget Climático v2
   Cards por estação + gráfico visual de
   temperatura + destaques inteligentes
   ═══════════════════════════════════════════ */

interface MesData {
  monthKey: string;
  temp: string;
  tempMin: number;
  tempMax: number;
  rain: 'high' | 'medium' | 'low';
  tide: 'high' | 'medium' | 'low';
  noteKey: string;
  icon: 'sun' | 'cloud' | 'rain';
  estacao: 'verao' | 'outono-primavera' | 'inverno';
}

const MESES: MesData[] = [
  {
    monthKey: 'home.season.months.jan',
    temp: '24–30°C',
    tempMin: 24,
    tempMax: 30,
    rain: 'high',
    tide: 'high',
    noteKey: 'widget.weather.note.highSeason',
    icon: 'sun',
    estacao: 'verao',
  },
  {
    monthKey: 'home.season.months.fev',
    temp: '24–30°C',
    tempMin: 24,
    tempMax: 30,
    rain: 'high',
    tide: 'high',
    noteKey: 'widget.weather.note.highSeason',
    icon: 'sun',
    estacao: 'verao',
  },
  {
    monthKey: 'home.season.months.mar',
    temp: '22–28°C',
    tempMin: 22,
    tempMax: 28,
    rain: 'medium',
    tide: 'medium',
    noteKey: 'widget.weather.note.great',
    icon: 'sun',
    estacao: 'outono-primavera',
  },
  {
    monthKey: 'home.season.months.abr',
    temp: '20–26°C',
    tempMin: 20,
    tempMax: 26,
    rain: 'medium',
    tide: 'medium',
    noteKey: 'widget.weather.note.great',
    icon: 'cloud',
    estacao: 'outono-primavera',
  },
  {
    monthKey: 'home.season.months.mai',
    temp: '18–24°C',
    tempMin: 18,
    tempMax: 24,
    rain: 'low',
    tide: 'low',
    noteKey: 'widget.weather.note.lowSeason',
    icon: 'cloud',
    estacao: 'outono-primavera',
  },
  {
    monthKey: 'home.season.months.jun',
    temp: '16–22°C',
    tempMin: 16,
    tempMax: 22,
    rain: 'low',
    tide: 'low',
    noteKey: 'widget.weather.note.lowSeason',
    icon: 'rain',
    estacao: 'inverno',
  },
  {
    monthKey: 'home.season.months.jul',
    temp: '15–21°C',
    tempMin: 15,
    tempMax: 21,
    rain: 'low',
    tide: 'low',
    noteKey: 'widget.weather.note.winterBreak',
    icon: 'rain',
    estacao: 'inverno',
  },
  {
    monthKey: 'home.season.months.ago',
    temp: '17–23°C',
    tempMin: 17,
    tempMax: 23,
    rain: 'low',
    tide: 'low',
    noteKey: 'widget.weather.note.lowSeason',
    icon: 'cloud',
    estacao: 'inverno',
  },
  {
    monthKey: 'home.season.months.set',
    temp: '19–25°C',
    tempMin: 19,
    tempMax: 25,
    rain: 'medium',
    tide: 'medium',
    noteKey: 'widget.weather.note.spring',
    icon: 'sun',
    estacao: 'outono-primavera',
  },
  {
    monthKey: 'home.season.months.out',
    temp: '21–27°C',
    tempMin: 21,
    tempMax: 27,
    rain: 'medium',
    tide: 'medium',
    noteKey: 'widget.weather.note.great',
    icon: 'sun',
    estacao: 'outono-primavera',
  },
  {
    monthKey: 'home.season.months.nov',
    temp: '22–28°C',
    tempMin: 22,
    tempMax: 28,
    rain: 'medium',
    tide: 'medium',
    noteKey: 'widget.weather.note.great',
    icon: 'sun',
    estacao: 'outono-primavera',
  },
  {
    monthKey: 'home.season.months.dez',
    temp: '23–29°C',
    tempMin: 23,
    tempMax: 29,
    rain: 'high',
    tide: 'high',
    noteKey: 'widget.weather.note.highSeason',
    icon: 'sun',
    estacao: 'verao',
  },
];

const ESTACOES = {
  verao: {
    label: 'Verão',
    meses: 'Dez – Mar',
    cor: 'bg-amber-50 border-amber-200',
    icone: <Sun className="w-5 h-5 text-amber-500" />,
    destaque: 'Melhor para praia',
  },
  'outono-primavera': {
    label: 'Outono/Primavera',
    meses: 'Abr – Mai, Set – Nov',
    cor: 'bg-teal-50 border-teal-200',
    icone: <Cloud className="w-5 h-5 text-teal-500" />,
    destaque: 'Melhor custo-benefício',
  },
  inverno: {
    label: 'Inverno',
    meses: 'Jun – Ago',
    cor: 'bg-ocean-50 border-ocean-200',
    icone: <CloudRain className="w-5 h-5 text-ocean" />,
    destaque: 'Ideal para trilhas',
  },
};

function ClimaIcon({ tipo }: { tipo: 'sun' | 'cloud' | 'rain' }) {
  if (tipo === 'sun') return <Sun className="w-4 h-4 text-amber-500" />;
  if (tipo === 'cloud') return <Cloud className="w-4 h-4 text-warm-gray" />;
  return <CloudRain className="w-4 h-4 text-ocean" />;
}

function BarraTemperatura({ min, max }: { min: number; max: number }) {
  const minAbs = 15;
  const maxAbs = 30;
  const left = ((min - minAbs) / (maxAbs - minAbs)) * 100;
  const width = ((max - min) / (maxAbs - minAbs)) * 100;

  return (
    <div className="h-2 bg-sand-100 rounded-full overflow-hidden relative">
      <div
        className="absolute h-full rounded-full bg-gradient-to-r from-teal-400 to-amber-400"
        style={{ left: `${left}%`, width: `${width}%` }}
      />
    </div>
  );
}

export function PrevisaoTempoWidget() {
  const { t } = useTranslation();
  const schema = previsaoTempoWidget.getSchema(
    'https://www.climatempo.com.br/previsao-do-tempo/cidade/2527/paranagua-pr'
  );

  const mesAtual = new Date().getMonth();
  const mesAtualData = MESES[mesAtual];

  return (
    <div
      className="bg-white rounded-2xl border border-sand-200 shadow-[0_4px_16px_rgba(0,0,0,0.06)] overflow-hidden"
      {...previsaoTempoWidget.getDataAttrs()}
    >
      {schema && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({ '@context': 'https://schema.org', ...schema })}
          </script>
        </Helmet>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-teal-deep to-ocean px-6 py-5 text-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
            <Thermometer className="w-5 h-5" />
          </div>
          <div>
            <h4 className="font-display text-lg tracking-wide">{t('widget.weather.title')}</h4>
            <p className="font-body text-xs text-teal-100">{t('widget.weather.subtitle')}</p>
          </div>
        </div>
      </div>

      <div className="p-6 space-y-5">
        {/* Cards de estação */}
        <div className="grid grid-cols-3 gap-3">
          {Object.entries(ESTACOES).map(([key, est]) => (
            <div
              key={key}
              className={`rounded-xl border p-3 text-center transition-all ${est.cor} ${
                key === mesAtualData.estacao
                  ? 'ring-2 ring-teal-light ring-offset-2 scale-[1.02] shadow-sm'
                  : 'opacity-80 hover:opacity-100'
              }`}
            >
              <div className="flex justify-center mb-1.5">{est.icone}</div>
              <p className="font-display text-xs tracking-wider text-warm-dark uppercase">
                {est.label}
              </p>
              <p className="font-body text-[10px] text-warm-gray mt-0.5">{est.meses}</p>
              {key === mesAtualData.estacao && (
                <p className="font-body text-[10px] text-teal-deep font-bold mt-1.5">
                  ✦ {est.destaque}
                </p>
              )}
            </div>
          ))}
        </div>

        {/* Destaque do mês atual */}
        <div className="bg-gradient-to-r from-amber-50 to-teal-50 rounded-xl p-4 border border-amber-200/50 flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-white shadow-sm flex items-center justify-center flex-shrink-0">
            <ClimaIcon tipo={mesAtualData.icon} />
          </div>
          <div className="flex-1">
            <p className="font-body text-xs text-warm-gray">
              {t(mesAtualData.monthKey)} — agora na ilha
            </p>
            <p className="font-display text-2xl text-warm-dark">{mesAtualData.temp}</p>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-1 text-ocean">
              <Droplets className="w-3 h-3" />
              <span className="font-body text-[10px] font-semibold">
                {t(`widget.weather.level.${mesAtualData.rain}`)}
              </span>
            </div>
            <div className="flex items-center gap-1 text-teal-deep mt-0.5">
              <Waves className="w-3 h-3" />
              <span className="font-body text-[10px] font-semibold">
                {t(`widget.weather.level.${mesAtualData.tide}`)}
              </span>
            </div>
          </div>
        </div>

        {/* Tabela com barras de temperatura */}
        <div>
          <p className="font-body text-xs font-semibold text-warm-dark mb-3 flex items-center gap-1">
            <Thermometer className="w-3 h-3 text-ocean" />
            Médias ao longo do ano
          </p>
          <div className="space-y-2">
            {MESES.map((m, i) => (
              <div
                key={m.monthKey}
                className={`flex items-center gap-3 rounded-lg px-2 py-1.5 transition-colors ${
                  i === mesAtual ? 'bg-teal-50/70' : 'hover:bg-sand-50'
                }`}
              >
                <span className="w-8 font-body text-xs font-medium text-warm-dark">
                  {t(m.monthKey)}
                </span>
                <div className="flex-1">
                  <BarraTemperatura min={m.tempMin} max={m.tempMax} />
                </div>
                <span className="w-14 text-right font-body text-xs text-warm-gray">{m.temp}</span>
                <ClimaIcon tipo={m.icon} />
              </div>
            ))}
          </div>
        </div>

        {/* Legendas */}
        <div className="flex items-center gap-4 text-[10px] font-body text-warm-gray">
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-amber-400" />
            Quente
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-teal-400" />
            Ameno
          </div>
          <div className="flex items-center gap-1">
            <div className="w-2 h-2 rounded-full bg-ocean" />
            Fresco
          </div>
        </div>

        {/* CTAs */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <a
            href="https://www.climatempo.com.br/previsao-do-tempo/cidade/1052/paranagua-pr"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => previsaoTempoWidget.trackClick({ destino: 'climatempo_paranagua' })}
            className="flex items-center justify-center gap-2 bg-teal-deep hover:bg-teal-dark text-white font-body font-bold rounded-xl px-4 py-3.5 transition-all text-sm shadow-sm hover:shadow-md"
          >
            <span>{t('widget.weather.ctaForecast')}</span>
            <ArrowUpRight className="w-4 h-4" />
          </a>

          <a
            href="https://www.mar.mil.br/dhn/chm/tabuas/"
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => previsaoTempoWidget.trackClick({ destino: 'tabua_mare_marinha' })}
            className="flex items-center justify-center gap-2 bg-white border-2 border-sand-200 text-warm-dark hover:bg-sand-50 hover:border-sand-300 font-body font-bold rounded-xl px-4 py-3.5 transition-all text-sm shadow-sm hover:shadow-md"
          >
            <Waves className="w-4 h-4 text-ocean" />
            <span>{t('widget.weather.ctaTide')}</span>
          </a>
        </div>

        <p className="font-body text-[11px] text-warm-gray text-center">
          {t('widget.weather.footerNote')}
        </p>
      </div>
    </div>
  );
}
